/**
 * GenericDrawer Component
 *
 * A wrapper drawer that can render any content.
 * Used as a bridge to convert existing tab content into drawers.
 *
 * @see docs/plans/DRAWER-SIDEBAR-CONVERSION-PLAN.md
 */

import React, { Suspense } from "react";
import { StackedDrawer, type DrawerWidth } from "@/components/ui/stacked-drawer";
import { useDrawerStack } from "@/contexts/DrawerStackContext";
import { DrawerSkeleton } from "./DrawerSkeleton";
import { DrawerErrorBoundary } from "./DrawerErrorBoundary";
import type { DrawerType } from "@/types/drawer";

// ============================================================================
// Types
// ============================================================================

export interface GenericDrawerProps {
  /** Unique drawer ID (assigned by DrawerStackContext) */
  drawerId: string;
  /** Drawer type for error tracking */
  drawerType: DrawerType;
  /** Title displayed in header */
  title: string;
  /** Optional subtitle */
  subtitle?: string;
  /** Width of the drawer */
  width?: DrawerWidth;
  /** Content to render inside the drawer */
  children: React.ReactNode;
  /** Optional footer content */
  footer?: React.ReactNode;
  /** Callback when drawer closes */
  onClose?: () => void;
  /** Contained mode - renders inside parent instead of portal */
  contained?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function GenericDrawer({
  drawerId,
  drawerType,
  title,
  subtitle,
  width = "xl",
  children,
  footer,
  onClose,
  contained = true, // Default to contained mode for streamlined look
}: GenericDrawerProps) {
  const { drawers, requestClose, removeDrawer } = useDrawerStack();
  const drawer = drawers.find((d) => d.id === drawerId);

  // If drawer not found in stack, don't render anything
  // This can happen briefly during cleanup
  if (!drawer) {
    return null;
  }

  const layer = drawer.layer;
  const isOpen = !drawer.isClosing;

  const handleClose = () => {
    onClose?.();
    requestClose(drawerId);
  };

  // Handle animation complete - remove drawer from stack after exit animation
  const handleAnimationComplete = (animationIsOpen: boolean) => {
    if (!animationIsOpen) {
      // Animation finished closing, now remove from stack
      removeDrawer(drawerId);
    }
  };

  return (
    <StackedDrawer
      id={drawerId}
      layer={layer}
      isOpen={isOpen}
      onClose={handleClose}
      onAnimationComplete={handleAnimationComplete}
      title={title}
      subtitle={subtitle}
      width={width}
      footer={footer}
      contained={contained}
    >
      <DrawerErrorBoundary drawerType={drawerType} onRetry={() => {}}>
        <Suspense fallback={<DrawerSkeleton type="generic" />}>
          {children}
        </Suspense>
      </DrawerErrorBoundary>
    </StackedDrawer>
  );
}

export default GenericDrawer;
